<template>
  <v-dialog
    :value="show"
    :max-width="maxWidth"
    :scrollable="scrollable"
    :persistent="persistent"
    transition="dialog-bottom-transition"
    content-class="app-dialog-content"
    @input="onInput"
    @click:outside="$emit('outside')"
    @keydown.esc="close"
  >
    <v-card rounded="xl" :class="title ? 'app-dialog' : ''">
      <!-- Header band -->
      <div v-if="title" class="app-dialog__header">
        <div class="app-dialog__icon" v-if="icon">
          <v-icon color="white" size="26">{{ icon }}</v-icon>
        </div>
        <div class="app-dialog__heading">
          <div class="app-dialog__title">{{ title }}</div>
          <div class="app-dialog__subtitle" v-if="subtitle || statusText">
            <span v-if="subtitle">{{ subtitle }}</span>
            <v-chip
              v-if="statusText"
              x-small
              class="app-dialog__status ml-2"
              :class="`app-dialog__status--${statusColor}`"
            >
              {{ statusText }}
            </v-chip>
          </div>
        </div>
        <v-spacer></v-spacer>
        <div class="app-dialog__header-actions">
          <slot name="header-actions"></slot>
        </div>
        <v-btn icon dark @click="close" class="ml-1">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- No title given: the slot content (an "embedded" page) already
           renders its own v-card + header/back-button, so this dialog stays
           a bare shell and doesn't double-wrap it in another card. -->
      <template v-if="title">
        <div class="app-dialog__body" :class="{ 'app-dialog__body--scrollable': scrollable }">
          <slot></slot>
        </div>
        <div class="app-dialog__footer" v-if="$slots.actions">
          <div class="app-dialog__footer-actions">
            <slot name="actions"></slot>
          </div>
          <v-spacer></v-spacer>
          <v-btn text @click="close">Close</v-btn>
        </div>
      </template>
      <slot v-else></slot>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "AppDialog",
  props: {
    show: { type: Boolean, required: true },
    title: { type: String, default: "" },
    subtitle: { type: String, default: "" },
    statusText: { type: String, default: "" },
    statusColor: { type: String, default: "grey" },
    icon: { type: String, default: "" },
    maxWidth: { type: [Number, String], default: 900 },
    scrollable: { type: Boolean, default: true },
    persistent: { type: Boolean, default: false },
  },
  methods: {
    onInput(value) {
      this.$emit("update:show", value);
      if (!value) this.$emit("close");
    },
    close() {
      this.$emit("update:show", false);
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.app-dialog {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
}
.app-dialog__header {
  flex: none;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  background: linear-gradient(135deg, var(--color-primary-dark) 0%, var(--color-primary) 100%);
  color: #fff;
}
.app-dialog__icon {
  flex: none;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
}
.app-dialog__heading {
  min-width: 0;
}
.app-dialog__title {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.app-dialog__subtitle {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  margin-top: 2px;
}
.app-dialog__status {
  font-weight: 700 !important;
  color: var(--color-ink) !important;
  background: #fff !important;
}
.app-dialog__status--error { background: #fff !important; color: var(--color-error) !important; }
.app-dialog__status--warning { background: #fff !important; color: #9a6a1f !important; }
.app-dialog__status--success { background: #fff !important; color: var(--color-success) !important; }
.app-dialog__header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.app-dialog__header-actions >>> .v-btn {
  color: #fff !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
}
.app-dialog__body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 24px;
  background: var(--color-surface);
}
.app-dialog__body--scrollable {
  overflow-y: auto;
}
.app-dialog__footer {
  flex: none;
  display: flex;
  align-items: center;
  padding: 14px 24px;
  border-top: 1px solid #ECE7DE;
  background: var(--color-surface);
}
.app-dialog__footer-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
</style>
